import { Button } from "@workspace/web-ui/components/button"
// import { Popover, PopoverContent, PopoverTrigger } from "@workspace/web-ui/components/popover"
import { useState, useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { icd10SearchQueryOptions, type ICD10SearchResult } from "../../api/queries"
import { Check, ChevronsUpDown, Search } from "lucide-react"
import { cn } from "~/lib/utils"

type ICD10FormData = {
    icd10_code?: string
}

type ICD10FormProps = {
    onSubmit: (data: ICD10FormData) => void
    onCancel: () => void
    isLoading?: boolean
    isEditing?: boolean
    onCodeChange?: (code: string | undefined) => void
}

export const ICD10Form = ({ onSubmit, onCancel, isLoading, isEditing, onCodeChange }: ICD10FormProps) => {
    const [open, setOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedCode, setSelectedCode] = useState<{ code: string; description: string; category?: string } | null>(null)
    const scrollableRef = useRef<HTMLDivElement>(null)

    // Debounce search term
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm)
        }, 300)
        return () => clearTimeout(timer)
    }, [searchTerm])

    // Handle wheel events to prevent bubbling to parent
    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const { data: searchResults = [], isLoading: isSearching } = useQuery(
        icd10SearchQueryOptions(debouncedSearchTerm)
    )

    const results: ICD10SearchResult[] = Array.isArray(searchResults) ? searchResults : []

    const handleSelectCode = (result: ICD10SearchResult) => {
        setSelectedCode({ code: result.code, description: result.description, category: result.category })
        onCodeChange?.(result.code)
        setOpen(false)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        onSubmit({ icd10_code: selectedCode?.code })
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="px-6 pt-4 pb-4 space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">ICD-10 Code (Optional)</label>
                    
                    {/* Replace Popover with inline expandable section */}
                    <div className="space-y-2">
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full justify-between h-10 text-left font-normal"
                            type="button"
                            onClick={() => setOpen(!open)}
                        >
                            <span className={cn("truncate", !selectedCode && "text-muted-foreground")}>
                                {selectedCode ? `${selectedCode.code} - ${selectedCode.description}` : "Search for ICD-10 code..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                        
                        {open && (
                            <div className="border rounded-md overflow-hidden">
                                <div className="flex flex-col h-[170px]">
                                    {/* Search Input */}
                                    <div className="flex items-center border-b px-3 py-2 shrink-0 bg-white">
                                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                        <input
                                            className="flex h-8 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                                            placeholder="Search conditions or symptoms..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                    </div>

                                    {/* Results List */}
                                    <div 
                                        ref={scrollableRef}
                                        className="overflow-y-auto flex-1 min-h-0 overscroll-contain bg-white"
                                        onWheel={handleWheel}
                                    >
                                        {isSearching ? (
                                            <div className="py-6 text-center text-sm text-muted-foreground">
                                                Searching...
                                            </div>
                                        ) : searchTerm.length < 2 ? (
                                            <div className="py-6 text-center text-sm text-muted-foreground">
                                                Type at least 2 characters to search
                                            </div>
                                        ) : results.length === 0 ? (
                                            <div className="py-6 text-center text-sm text-muted-foreground">
                                                No ICD-10 codes found.
                                            </div>
                                        ) : (
                                            <div className="p-1">
                                                {results.map((result: ICD10SearchResult) => (
                                                    <div
                                                        key={result.code}
                                                        className={cn(
                                                            "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                                            "transition-colors"
                                                        )}
                                                        onClick={() => handleSelectCode(result)}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4 shrink-0",
                                                                selectedCode?.code === result.code ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <div className="flex flex-col flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium">{result.code}</span>
                                                                {result.category && (
                                                                    <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-muted rounded">
                                                                        {result.category}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-sm text-muted-foreground truncate">
                                                                {result.description}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {!isEditing && (
              <div className="shrink-0 flex justify-end w-full gap-2 px-6 py-4 border-t bg-white">
                <Button type="button" className="button-primary w-full" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button 
                    type="submit" 
                    className="bg-[#427DDD] !border-none text-white !rounded-lg !shadow-none w-full" 
                    disabled={isLoading}
                >
                    {isLoading ? "Processing..." : "Generate"}
                </Button>
              </div>
            )}
        </form>
    )
}